/** @jsxImportSource ../../i18n */
import React from 'react';
import { Area, CartesianGrid, ComposedChart, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { usePrivacy } from '../../contexts/PrivacyContext';
import { usePrefersReducedMotion } from '../../theme/motion';
import { formatMoney } from '../../theme/format';
import { MASKED_AMOUNT } from '../ui/Money';
import { axisProps, chartColors, cursorProps, gridProps } from './chartTheme';

const FanTooltip = ({ active, payload, label, masked }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  const money = (v) => (masked ? MASKED_AMOUNT : formatMoney(v, { decimals: 0 }));
  return (
    <div className="min-w-[11rem] rounded-control border border-line-strong bg-surface-3/95 px-3 py-2.5 text-sm shadow-pop backdrop-blur-md">
      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-fg-subtle">{label}</p>
      <dl className="mt-1.5 space-y-1">
        <div className="flex justify-between gap-4"><dt className="text-fg-muted">Défavorable (1 sur 10)</dt><dd className="num text-fg">{money(d.p10)}</dd></div>
        <div className="flex justify-between gap-4"><dt className="text-fg-muted">Médian</dt><dd className="num font-semibold text-fg">{money(d.p50)}</dd></div>
        <div className="flex justify-between gap-4"><dt className="text-fg-muted">Favorable (1 sur 10)</dt><dd className="num text-fg">{money(d.p90)}</dd></div>
      </dl>
    </div>
  );
};

// Fan of a Monte Carlo projection: the 10-90 % and 25-75 % bands, the median
// and the target. Decorative for screen readers: the figures are in the text.
export const FanChart = ({ data, target, height = 320, className }) => {
  const { hidden } = usePrivacy();
  const reduced = usePrefersReducedMotion();
  const rows = data.map((d) => ({ ...d, band90: [d.p10, d.p90], band50: [d.p25, d.p75] }));
  return (
    <div className={className} style={{ height }} aria-hidden="true">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={rows} margin={{ top: 8, right: 4, bottom: 0, left: 0 }}>
          <CartesianGrid {...gridProps} />
          <XAxis dataKey="label" {...axisProps} minTickGap={24} tickMargin={10} />
          <YAxis {...axisProps} orientation="right" width={64} tickMargin={6} tickFormatter={(v) => (hidden ? '•••' : formatMoney(v, { compact: true }))} />
          <Tooltip cursor={cursorProps} content={<FanTooltip masked={hidden} />} />
          <Area dataKey="band90" stroke="none" fill={chartColors.gold} fillOpacity={0.12} isAnimationActive={!reduced} />
          <Area dataKey="band50" stroke="none" fill={chartColors.gold} fillOpacity={0.22} isAnimationActive={!reduced} />
          <Line dataKey="p50" stroke={chartColors.gold} strokeWidth={2} dot={false} isAnimationActive={!reduced} />
          {target > 0 && <ReferenceLine y={target} stroke={chartColors.gainText} strokeDasharray="5 5" ifOverflow="extendDomain" />}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};
